import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import vendorService from '../services/vendorService';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaFileDownload, FaSpinner, FaUpload, FaTimes, FaBuilding, FaChevronRight } from 'react-icons/fa';
import './EmployeePurchasePrograms.css';
import { useToast } from '../context/ToastContext';
import { extractErrorMessage } from '../utils/errorUtils';
import { AuthContext } from '../context/AuthContext';
import { can, FEATURE_KEYS } from '../utils/featureAccess';

const EmployeePurchasePrograms = () => {
    const { addToast } = useToast();
    const location = useLocation();
    const { user } = useContext(AuthContext);

    // Coarse feature capabilities rather than per-permission-name checks, matching
    // FolderList / KnowledgeBase / PasswordVault. Read-only roles (Sales, Ecom)
    // hold only `core.vendors.vendor.read`, so they get no write/delete controls.
    const canWrite = can(user, FEATURE_KEYS.VENDOR_PURCHASE_PROGRAM, 'write');
    const canDeleteItems = can(user, FEATURE_KEYS.VENDOR_PURCHASE_PROGRAM, 'delete');

    const [allVendors, setAllVendors] = useState([]);
    const [filteredVendors, setFilteredVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVendor, setSelectedVendor] = useState(null);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [vendorForm, setVendorForm] = useState({ vendorName: '', instructions: '' });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Custom confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    useEffect(() => {
        fetchVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Real-time search: filter locally on each keystroke
    const handleSearch = useCallback((e) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (!term.trim()) {
            setFilteredVendors(allVendors);
        } else {
            setFilteredVendors(
                allVendors.filter(v =>
                    v.vendorName.toLowerCase().includes(term.toLowerCase())
                )
            );
        }
        setSelectedVendor(null);
    }, [allVendors]);

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const data = await vendorService.getAllVendors();
            const sorted = [...data].sort((a, b) => a.vendorName.localeCompare(b.vendorName));
            setAllVendors(sorted);
            setFilteredVendors(sorted);

            const params = new URLSearchParams(location.search);
            const vendorId = params.get('vendorId');

            if (vendorId) {
                const found = sorted.find(v => v.id === vendorId);
                if (found) {
                    setSelectedVendor(found);
                } else if (sorted.length > 0) {
                    setSelectedVendor(sorted[0]);
                }
            } else if (sorted.length > 0 && !selectedVendor) {
                setSelectedVendor(sorted[0]);
            }
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, 'Failed to fetch vendor programs');
            addToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectVendor = (vendor) => {
        setSelectedVendor(vendor);
    };

    const handleOpenModal = (vendor = null) => {
        if (vendor) {
            setEditingVendor(vendor);
            setVendorForm({ vendorName: vendor.vendorName, instructions: vendor.instructions || '' });
        } else {
            setEditingVendor(null);
            setVendorForm({ vendorName: '', instructions: '' });
        }
        setSelectedFile(null);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingVendor(null);
        setVendorForm({ vendorName: '', instructions: '' });
        setSelectedFile(null);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            let savedVendor;
            if (editingVendor) {
                savedVendor = await vendorService.updateVendor(editingVendor.id, vendorForm);
                addToast('Vendor updated successfully!', 'success');
            } else {
                savedVendor = await vendorService.createVendor(vendorForm);
                addToast('Vendor created!', 'success');
            }
            if (selectedFile) {
                await vendorService.uploadFile(savedVendor.id, selectedFile);
                addToast('File uploaded!', 'success');
            }
            handleCloseModal();

            // Re-fetch to get the vendor with the newly attached files
            const updatedVendors = await vendorService.getAllVendors();
            const sorted = [...updatedVendors].sort((a, b) => a.vendorName.localeCompare(b.vendorName));
            setAllVendors(sorted);
            setFilteredVendors(sorted);

            const updatedVendor = sorted.find(v => v.id === savedVendor.id);
            setSelectedVendor(updatedVendor || savedVendor);
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, 'Error saving vendor');
            addToast(errorMessage, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteVendor = (id) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Delete Vendor',
            message: 'Are you sure you want to delete this vendor program? This action cannot be undone.',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    await vendorService.deleteVendor(id);
                    addToast('Vendor deleted', 'success');
                    setSelectedVendor(null);
                    fetchVendors();
                } catch (error) {
                    const errorMessage = await extractErrorMessage(error, 'Error deleting vendor');
                    addToast(errorMessage, 'error');
                }
            }
        });
    };

    const handleDeleteFile = (vendorId, fileKey) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Delete File',
            message: 'Are you sure you want to delete this file? This action cannot be undone.',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const updated = await vendorService.deleteFile(vendorId, fileKey);
                    addToast('File deleted', 'success');
                    setSelectedVendor(updated);

                    // If we're also currently editing this vendor in the modal, update it there too
                    if (editingVendor && editingVendor.id === vendorId) {
                        setEditingVendor(updated);
                    }

                    fetchVendors();
                } catch (error) {
                    const errorMessage = await extractErrorMessage(error, 'Error deleting file');
                    addToast(errorMessage, 'error');
                }
            }
        });
    };

    const handleDownload = async (fileUrl, fileName) => {
        try {
            const blob = await vendorService.downloadFile(fileUrl);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            const errorMessage = await extractErrorMessage(error, 'Error downloading file');
            addToast(errorMessage, 'error');
        }
    };

    const getInitials = (name) => name ? name.charAt(0).toUpperCase() : '?';

    return (
        <div className="epp-page">
            {/* Page header band */}
            <div className="epp-page-header">
                <h2>Purchase Programs</h2>
                <div className="epp-page-header-actions">
                    {canWrite && (
                        <button className="epp-add-btn" onClick={() => handleOpenModal()} title="Add Vendor">
                            <FaPlus />
                        </button>
                    )}
                </div>
            </div>

            <div className="epp-container">
                {/* Left Panel: Vendor List */}
                <div className="epp-sidebar">
                    <div className="epp-panel-strip epp-sidebar-header">
                        <div className="epp-search-wrap">
                            <FaSearch className="epp-search-icon" />
                            <input
                                type="text"
                                className="epp-search-input"
                                placeholder="Search vendors..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            {searchTerm && (
                                <button className="epp-search-clear" onClick={() => { setSearchTerm(''); setFilteredVendors(allVendors); }}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="epp-vendor-list">
                        {loading ? (
                            <div className="epp-loading"><FaSpinner className="epp-spinner" /></div>
                        ) : filteredVendors.length === 0 ? (
                            <div className="epp-empty">No vendors found</div>
                        ) : (
                            filteredVendors.map(vendor => (
                                <button
                                    key={vendor.id}
                                    className={`epp-vendor-item ${selectedVendor?.id === vendor.id ? 'active' : ''}`}
                                    onClick={() => handleSelectVendor(vendor)}
                                >
                                    <span className="epp-vendor-avatar">{getInitials(vendor.vendorName)}</span>
                                    <span className="epp-vendor-name">{vendor.vendorName}</span>
                                    {vendor.files && vendor.files.length > 0 && (
                                        <span className="epp-file-badge">{vendor.files.length}</span>
                                    )}
                                    <FaChevronRight className="epp-vendor-arrow" />
                                </button>
                            ))
                        )}
                    </div>

                    <div className="epp-sidebar-footer">
                        {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Right Panel: Vendor Detail */}
                <div className="epp-detail">
                    {!selectedVendor ? (
                        <div className="epp-placeholder">
                            <FaBuilding className="epp-placeholder-icon" />
                            <p>Select a vendor to view details</p>
                        </div>
                    ) : (
                        <div className="epp-detail-content">
                            <div className="epp-panel-strip epp-detail-header">
                                <div className="epp-detail-title-row">
                                    <div className="epp-detail-avatar-lg">{getInitials(selectedVendor.vendorName)}</div>
                                    <div>
                                        <h1 className="epp-detail-title">{selectedVendor.vendorName}</h1>
                                        <span className="epp-detail-subtitle">Employee Purchase Program</span>
                                    </div>
                                </div>
                                <div className="epp-detail-actions">
                                    {canWrite && (
                                        <button className="epp-action-btn edit" onClick={() => handleOpenModal(selectedVendor)}>
                                            <FaEdit /> Edit
                                        </button>
                                    )}
                                    {canDeleteItems && (
                                        <button className="epp-action-btn delete" onClick={() => handleDeleteVendor(selectedVendor.id)}>
                                            <FaTrash /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="epp-detail-body">
                                {/* Instructions Section */}
                                <section className="epp-section epp-panel">
                                    <div className="epp-panel-strip">
                                        <h3 className="epp-section-title">How to Purchase</h3>
                                    </div>
                                    <div className="epp-instructions">
                                        {selectedVendor.instructions
                                            ? <p style={{ whiteSpace: 'pre-wrap' }}>{selectedVendor.instructions}</p>
                                            : <p className="epp-no-content">No instructions provided.</p>
                                        }
                                    </div>
                                </section>

                                {/* Files Section */}
                                <section className="epp-section epp-panel">
                                    <div className="epp-panel-strip epp-section-title-row">
                                        <h3 className="epp-section-title">Forms & Documents</h3>
                                        {canWrite && (
                                            <label className="epp-upload-label" title="Upload a file">
                                                <FaUpload />
                                                <span>Upload</span>
                                                <input
                                                    type="file"
                                                    style={{ display: 'none' }}
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        try {
                                                            await vendorService.uploadFile(selectedVendor.id, file);
                                                            addToast('File uploaded!', 'success');
                                                            const updated = await vendorService.getAllVendors();
                                                            const found = updated.find(v => v.id === selectedVendor.id);
                                                            if (found) setSelectedVendor(found);
                                                            setAllVendors([...updated].sort((a, b) => a.vendorName.localeCompare(b.vendorName)));
                                                            setFilteredVendors([...updated].sort((a, b) => a.vendorName.localeCompare(b.vendorName)));
                                                        } catch (error) {
                                                            const errorMessage = await extractErrorMessage(error, 'Upload failed');
                                                            addToast(errorMessage, 'error');
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                    {selectedVendor.files && selectedVendor.files.length > 0 ? (
                                        <div className="epp-files-list">
                                            {selectedVendor.files.map((file, idx) => (
                                                <div key={idx} className="epp-file-row">
                                                    <button onClick={() => handleDownload(file.url, file.name)} className="epp-file-link" style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                                        <FaFileDownload className="epp-file-icon" />
                                                        <div>
                                                            <span className="epp-file-name">{file.name}</span>
                                                            {file.uploadedAt && (
                                                                <span className="epp-file-date">Uploaded {new Date(file.uploadedAt).toLocaleDateString()}</span>
                                                            )}
                                                        </div>
                                                    </button>
                                                    {canDeleteItems && (
                                                        <button className="epp-file-delete" onClick={() => handleDeleteFile(selectedVendor.id, file.fileKey)}>
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="epp-no-content">No documents attached.</p>
                                    )}
                                </section>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="epp-modal-overlay" onClick={handleCloseModal}>
                    <div className="epp-modal" onClick={e => e.stopPropagation()}>
                        <div className="epp-modal-header">
                            <h2>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
                            <button className="epp-modal-close" onClick={handleCloseModal}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleFormSubmit} className="epp-modal-form">
                            <div className="epp-field">
                                <label>Vendor Name</label>
                                <input
                                    type="text"
                                    required
                                    value={vendorForm.vendorName}
                                    onChange={e => setVendorForm({ ...vendorForm, vendorName: e.target.value })}
                                    placeholder="e.g. Dansko"
                                />
                            </div>
                            <div className="epp-field">
                                <label>Instructions / Program Details</label>
                                <textarea
                                    rows="6"
                                    value={vendorForm.instructions}
                                    onChange={e => setVendorForm({ ...vendorForm, instructions: e.target.value })}
                                    placeholder="How can employees purchase from this vendor?"
                                />
                            </div>

                            {/* Existing Files inside Modal */}
                            {editingVendor && editingVendor.files && editingVendor.files.length > 0 && (
                                <div className="epp-field" style={{ marginBottom: '8px' }}>
                                    <label>Attached Files</label>
                                    <div className="epp-files-list" style={{ marginTop: '4px' }}>
                                        {editingVendor.files.map((file, idx) => (
                                            <div key={idx} className="epp-file-row" style={{ padding: '8px 12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                                    <FaFileDownload className="epp-file-icon" style={{ fontSize: '1rem' }} />
                                                    <span className="epp-file-name" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {file.name}
                                                    </span>
                                                </div>
                                                {canDeleteItems && (
                                                    <button type="button" className="epp-file-delete" style={{ width: '24px', height: '24px' }} onClick={() => handleDeleteFile(editingVendor.id, file.fileKey)}>
                                                        <FaTimes style={{ fontSize: '0.65rem' }} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="epp-field">
                                <label>Attach New File (Optional)</label>
                                <label className="epp-file-drop">
                                    <input type="file" style={{ display: 'none' }} onChange={e => setSelectedFile(e.target.files[0])} />
                                    <FaUpload />
                                    <span>{selectedFile ? selectedFile.name : 'Click to choose a file'}</span>
                                </label>
                            </div>
                            <div className="epp-modal-footer">
                                <button type="button" className="epp-btn-ghost" onClick={handleCloseModal}>Cancel</button>
                                <button type="submit" className="epp-btn-primary" disabled={isSaving}>
                                    {isSaving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : 'Save Vendor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Confirmation Modal */}
            {confirmDialog.isOpen && (
                <div className="epp-modal-overlay">
                    <div className="epp-modal confirm" style={{ maxWidth: '400px' }}>
                        <div className="epp-modal-header" style={{ paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <h2 style={{ fontSize: '1.1rem' }}>{confirmDialog.title}</h2>
                        </div>
                        <div className="epp-modal-form" style={{ padding: '24px' }}>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                {confirmDialog.message}
                            </p>
                            <div className="epp-modal-footer" style={{ marginTop: '24px' }}>
                                <button
                                    className="epp-btn-ghost"
                                    onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="epp-btn-primary"
                                    onClick={confirmDialog.onConfirm}
                                    style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.2)' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeePurchasePrograms;
